import React from 'react';
// Generated geometry (Natural Earth via scripts/generate-globe-paths.mjs):
// land silhouette, graticule, merged EU-27 polygon, UK polygon, and projected
// port coordinates — see docs/features/signin-globe-accuracy/.
import { LAND, GRATICULE, EUROPE, UK, PORTS } from './worldTradePaths';

// Decorative world-trade scene for the auth brand panel: an Atlantic-centered
// globe with stylized continents, and trade routes from every continent
// converging on the EU + UK highlight, where the hub sits.
//
// Constraints the markup encodes:
// - Purely decorative: aria-hidden, no focusable content, no text at all.
// - All motion is CSS-driven and gated behind prefers-reduced-motion.
// - Freight dots ride the arcs via offset-path; browsers without support
//   keep them hidden (@supports) instead of stacking them at the origin.
// - Accent color follows the runtime brand via currentColor +
//   --primary-color, so whitelabel tenants get their own palette. Brands
//   whose primary is itself near-black (isDarkSurfaceWhiteTextBrand) pass
//   whiteAccent so the accents stay visible against the dark panel.

// The hub is Rotterdam — the EU entry port every route flows into.
const HUB = { x: PORTS.rotterdam[0], y: PORTS.rotterdam[1] };

const GLOBE_CENTER = { x: 450, y: 560 };

// Quadratic arc between two projected points, bowing away from the globe
// center so routes read as great-circle-ish hops above the surface.
//
// The bow is a share of the chord rather than a constant: the Mediterranean
// feeders are short (Algiers→Rotterdam is 86px against Santos' 381px), and a
// fixed push threw them into a loop wider than the route itself. Capped so the
// deep-sea arcs keep the curvature they were designed with.
const BOW_RATIO = 0.35;
const BOW_MAX = 90;
const arcPath = ([x1, y1], [x2, y2]) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = mx - GLOBE_CENTER.x;
  const dy = my - GLOBE_CENTER.y;
  const len = Math.hypot(dx, dy) || 1;
  const push = Math.min(BOW_MAX, Math.hypot(x2 - x1, y2 - y1) * BOW_RATIO);
  return `M${x1},${y1} Q${mx + (dx / len) * push},${my + (dy / len) * push} ${x2},${y2}`;
};

// Each route starts at a port and ends at the hub. pathLength is normalized to
// 390 on the flow overlays so one dasharray/keyframe pair serves every arc.
//
// Two tiers (see CITIES in scripts/generate-globe-paths.mjs). `hub` is a
// deep-sea origin: full-weight arc, pulse ring, large freight dot. `feeder` is
// a short-haul Mediterranean route — drawn lighter and without a pulse ring
// because it terminates only ~90-160px from the hub, and a full-weight ring
// that close crowds the twelve-star ring instead of reading as its own origin.
const ROUTES = [
  { from: 'shanghai', tier: 'hub', dur: 9, delay: 0 },
  { from: 'mumbai', tier: 'hub', dur: 11, delay: 2.5 },
  { from: 'newYork', tier: 'hub', dur: 10, delay: 4 },
  { from: 'santos', tier: 'hub', dur: 12, delay: 1 },
  { from: 'recife', tier: 'hub', dur: 11.5, delay: 5.5 },
  { from: 'lagos', tier: 'hub', dur: 13, delay: 6 },
  { from: 'abuDhabi', tier: 'hub', dur: 10.5, delay: 3.2 },
  { from: 'istanbul', tier: 'feeder', dur: 6, delay: 1.6 },
  { from: 'alexandria', tier: 'feeder', dur: 7, delay: 4.6 },
  { from: 'algiers', tier: 'feeder', dur: 5.5, delay: 3.8 },
];

// Per-tier line and marker weights.
const TIERS = {
  hub: {
    track: 'rgba(255,255,255,0.10)',
    trackWidth: 1.5,
    flowWidth: 2,
    flowOpacity: 0.55,
    cargo: 4,
    marker: 4,
    pulse: true,
  },
  feeder: {
    track: 'rgba(255,255,255,0.07)',
    trackWidth: 1,
    flowWidth: 1.4,
    flowOpacity: 0.42,
    cargo: 2.75,
    marker: 2.75,
    pulse: false,
  },
};

const ARCS = ROUTES.map((route) => ({
  ...route,
  d: arcPath(PORTS[route.from], PORTS.rotterdam),
}));

const PORT_POINTS = ROUTES.map((route) => ({
  x: PORTS[route.from][0],
  y: PORTS[route.from][1],
}));

const WorldTradeIllustration = ({ whiteAccent = false }) => (
  <svg
    className="wt-root absolute inset-0 h-full w-full"
    viewBox="0 0 900 1000"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
    focusable="false"
  >
    <style>{`
      .wt-root { color: ${whiteAccent ? '#fff' : 'rgb(var(--primary-color, 0 184 148))'}; }
      .wt-cargo { visibility: hidden; }
      @supports (offset-path: path('M0,0 L1,1')) {
        @media (prefers-reduced-motion: no-preference) {
          .wt-cargo { visibility: visible; animation: wt-travel linear infinite; }
        }
      }
      @media (prefers-reduced-motion: no-preference) {
        .wt-flow { animation: wt-flow linear infinite; }
        .wt-pulse { animation: wt-pulse 3.6s ease-out infinite; }
      }
      .wt-pulse { transform-box: fill-box; transform-origin: center; }
      @keyframes wt-flow { from { stroke-dashoffset: 390; } to { stroke-dashoffset: 0; } }
      @keyframes wt-travel {
        0% { offset-distance: 0%; opacity: 0; }
        10% { opacity: 1; }
        90% { opacity: 1; }
        100% { offset-distance: 100%; opacity: 0; }
      }
      @keyframes wt-pulse {
        0% { transform: scale(0.4); opacity: 0.6; }
        70% { transform: scale(1.7); opacity: 0; }
        100% { transform: scale(1.7); opacity: 0; }
      }
    `}</style>

    <defs>
      <radialGradient
        id="wt-glow"
        cx="0.5"
        cy="0.5"
        r="0.5"
      >
        <stop
          offset="0%"
          stopColor="currentColor"
          stopOpacity="0.22"
        />
        <stop
          offset="100%"
          stopColor="currentColor"
          stopOpacity="0"
        />
      </radialGradient>
      {/* Sea shading: blue-grey, lit slightly above center so the disc reads
          as a planet rather than a flat radar scope. Same ~210° hue family as
          the fixed ink-navy panel behind it (AuthShell PANEL_BG), so panel →
          sea edge → sea center → land is one brightness ladder in one hue —
          contrast comes from luminance steps, not hue fights, and the muted
          chroma leaves currentColor as the only saturated element (the
          whitelabel guarantee). The edge stop stays near the panel color so
          the rim doesn't halo. */}
      <radialGradient
        id="wt-sea"
        cx="0.5"
        cy="0.42"
        r="0.75"
      >
        <stop
          offset="0%"
          stopColor="#4a6172"
        />
        <stop
          offset="55%"
          stopColor="#394956"
        />
        <stop
          offset="100%"
          stopColor="#222d35"
        />
      </radialGradient>
      <clipPath id="wt-globe-clip">
        <circle
          cx="450"
          cy="560"
          r="330"
        />
      </clipPath>
    </defs>

    {/* Ambient glow behind the hub */}
    <circle
      cx={HUB.x}
      cy={HUB.y}
      r="380"
      fill="url(#wt-glow)"
    />

    {/* Globe: rim, graticule and continents (generated, pre-clipped by the
        projection's clipAngle; the clipPath stays as a safety net) */}
    <g clipPath="url(#wt-globe-clip)">
      {/* Charcoal sea + brighter grey land: the original disc (white 2% sea,
          white 9% land) read as a radar scope. The brightness split — shaded
          sea, land at white 18% over it — makes it read as ocean and
          continents while staying greyscale-neutral for whitelabel. */}
      <circle
        cx="450"
        cy="560"
        r="330"
        fill="url(#wt-sea)"
      />
      <path
        d={GRATICULE}
        fill="none"
        stroke="rgba(255,255,255,0.08)"
      />
      <path
        d={LAND}
        fill="rgba(255,255,255,0.18)"
      />
      {/* Covered jurisdictions: EU-27 and the UK, drawn identically (both are
          CBAM regimes the product files for) but kept as separate paths so
          they can diverge — e.g. if the UK should read as "from 2027" — with
          no regeneration.

          This fill and the trade flows are the ONLY brand-colored elements in
          the scene (design decision): the covered jurisdictions glow in the
          tenant's color, with every animated flow converging on them. The
          border stays white for every tenant — it delineates the shapes
          against sea and land alike, and keeps working for near-black
          primaries, whose whiteAccent fallback also turns the fill white. */}
      <path
        d={EUROPE}
        fill="currentColor"
        fillOpacity="0.45"
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="1.25"
      />
      <path
        d={UK}
        fill="currentColor"
        fillOpacity="0.45"
        stroke="rgba(255,255,255,0.6)"
        strokeWidth="1.25"
      />
    </g>
    <circle
      cx="450"
      cy="560"
      r="330"
      fill="none"
      stroke="rgba(255,255,255,0.18)"
    />

    {/* Trade routes: static track + animated flow + travelling freight dot */}
    {ARCS.map((arc, i) => {
      const tier = TIERS[arc.tier];
      return (
        <g key={arc.d}>
          <path
            d={arc.d}
            fill="none"
            stroke={tier.track}
            strokeWidth={tier.trackWidth}
          />
          <path
            className="wt-flow"
            d={arc.d}
            pathLength="390"
            fill="none"
            stroke="currentColor"
            strokeOpacity={tier.flowOpacity}
            strokeWidth={tier.flowWidth}
            strokeLinecap="round"
            strokeDasharray="26 364"
            style={{ animationDuration: `${arc.dur}s`, animationDelay: `${arc.delay}s` }}
          />
          <circle
            className="wt-cargo"
            r={tier.cargo}
            fill="#fff"
            fillOpacity="0.9"
            style={{
              offsetPath: `path('${arc.d}')`,
              offsetRotate: '0deg',
              animationDuration: `${arc.dur}s`,
              animationDelay: `${arc.delay}s`,
            }}
          />
          <g>
            {tier.pulse && (
              <circle
                className="wt-pulse"
                cx={PORT_POINTS[i].x}
                cy={PORT_POINTS[i].y}
                r="10"
                fill="none"
                stroke="rgba(255,255,255,0.35)"
                style={{ animationDelay: `${arc.delay}s` }}
              />
            )}
            <circle
              cx={PORT_POINTS[i].x}
              cy={PORT_POINTS[i].y}
              r={tier.marker}
              fill="rgba(255,255,255,0.75)"
            />
          </g>
        </g>
      );
    })}

    {/* European hub: pulse rings and core */}
    <g>
      <circle
        className="wt-pulse"
        cx={HUB.x}
        cy={HUB.y}
        r="16"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.6"
      />
      <circle
        className="wt-pulse"
        cx={HUB.x}
        cy={HUB.y}
        r="16"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.6"
        style={{ animationDelay: '1.8s' }}
      />
      <circle
        cx={HUB.x}
        cy={HUB.y}
        r="7"
        fill="currentColor"
      />
      <circle
        cx={HUB.x}
        cy={HUB.y}
        r="2.5"
        fill="#fff"
      />
    </g>
  </svg>
);

export default WorldTradeIllustration;
